import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Fade
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  CalendarToday as CalendarIcon,
  SwapHoriz as TransferIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import PageHeader from '../components/common/PageHeader';
import AlertMessage from '../components/common/AlertMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { TransferService } from '../services/transfer.service';
import { TableService } from '../services/table.service';
import { TableDataService } from '../services/tableData.service';
import { DataTransfer, TransferStatus } from '../types/transfer.types';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/slices/authSlice';
import dayjs from 'dayjs';

export default function TransferView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  
  const [transfer, setTransfer] = useState<DataTransfer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    status: TransferStatus | null;
    title: string;
    message: string;
  }>({
    open: false,
    status: null,
    title: '',
    message: ''
  });
  const [mounted, setMounted] = useState(false);
  
  const isSender = user?.id === transfer?.sourceUserId;
  const isReceiver = user?.id === transfer?.targetUserId;
  
  useEffect(() => {
    setMounted(true);
    if (id) {
      fetchTransfer(id);
    }
  }, [id]);
  
  const fetchTransfer = async (transferId: string) => {
    try {
      setLoading(true);
      const data = await TransferService.getTransferById(transferId);
      setTransfer(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных передачи');
    } finally {
      setLoading(false);
    }
  };
  
  const handleStatusDialogOpen = (status: TransferStatus) => {
    let title = '';
    let message = '';
    
    switch (status) {
      case TransferStatus.COMPLETED:
        title = 'Подтверждение принятия';
        message = 'Вы уверены, что хотите принять эту передачу? Данные будут добавлены в ваши таблицы.';
        break;
      case TransferStatus.REJECTED:
        title = 'Подтверждение отклонения';
        message = 'Вы уверены, что хотите отклонить эту передачу?';
        break;
      case TransferStatus.CANCELLED:
        title = 'Подтверждение отмены';
        message = 'Вы уверены, что хотите отменить эту передачу?';
        break;
    }
    
    setStatusDialog({ open: true, status, title, message });
  };
  
  const handleStatusDialogClose = () => {
    setStatusDialog({ open: false, status: null, title: '', message: '' });
  };
  
  const handleUpdateStatus = async () => {
    if (!id || !statusDialog.status) return;
    
    try {
      const updatedTransfer = await TransferService.updateTransferStatus(id, { 
        status: statusDialog.status 
      });
      
      setTransfer(updatedTransfer);
      setSuccess(`Статус передачи успешно обновлен на "${getStatusText(statusDialog.status)}"`);
      handleStatusDialogClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка обновления статуса');
    }
  };
  
  const getStatusText = (status: TransferStatus) => {
    const texts = {
      [TransferStatus.PENDING]: 'В ожидании',
      [TransferStatus.COMPLETED]: 'Завершено',
      [TransferStatus.REJECTED]: 'Отклонено',
      [TransferStatus.CANCELLED]: 'Отменено',
    };
    return texts[status] || status;
  };
  
  const getStatusConfig = (status: TransferStatus) => {
    const configs = {
      [TransferStatus.PENDING]: { color: '#ff9800', label: 'В ожидании' },
      [TransferStatus.COMPLETED]: { color: '#4caf50', label: 'Завершено' },
      [TransferStatus.REJECTED]: { color: '#f44336', label: 'Отклонено' },
      [TransferStatus.CANCELLED]: { color: '#9e9e9e', label: 'Отменено' },
    };
    return configs[status] || configs[TransferStatus.PENDING];
  };
  
  const handleViewItem = (tableId: string, dataId: string) => {
    navigate(`/tables/${tableId}/data/${dataId}`);
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} sx={{ color: '#667eea' }} />
      </Box>
    );
  }
  
  if (!transfer) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          Передача не найдена или у вас нет доступа к ней
        </Typography>
        <Button variant="contained" onClick={() => navigate('/transfers')} sx={{ mt: 2 }}>
          Вернуться к списку передач
        </Button>
      </Box>
    );
  }
  
  const statusConfig = getStatusConfig(transfer.status);
  
  return (
    <Box>
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${statusConfig.color}dd 0%, ${statusConfig.color}99 100%)`,
            borderRadius: 3,
            p: 4,
            mb: 3,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <TransferIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Просмотр передачи
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  ID: {transfer.id.substring(0, 16)}...
                </Typography>
              </Box>
              <Chip 
                label={statusConfig.label}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: statusConfig.color,
                  fontWeight: 700,
                  fontSize: '1rem',
                  height: 36,
                  px: 2
                }}
              />
            </Box>
          </Box>
        </Box>
      </Fade>
      
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Fade in={mounted} timeout={600}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 2.5, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InfoIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Основная информация
                  </Typography>
                </Box>
              </Box>
              
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Дата создания
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {dayjs(transfer.createdAt).format('DD.MM.YYYY HH:mm')}
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      Дата обновления
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {dayjs(transfer.updatedAt).format('DD.MM.YYYY HH:mm')}
                  </Typography>
                </Box>
                
                {transfer.description && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Описание
                      </Typography>
                    </Box>
                    <Typography variant="body1">
                      {transfer.description}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <Fade in={mounted} timeout={700}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <Box sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', p: 2.5, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Отправитель
                  </Typography>
                </Box>
              </Box>
              
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(79, 172, 254, 0.1)', color: '#4facfe', width: 48, height: 48 }}>
                    {transfer.sourceUser?.fullName?.charAt(0) || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {transfer.sourceUser?.fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      @{transfer.sourceUser?.username}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {transfer.sourceUser?.email}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
        
        <Grid size={{ xs: 12, md: 4 }}>
          <Fade in={mounted} timeout={800}>
            <Card sx={{ borderRadius: 3, height: '100%' }}>
              <Box sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', p: 2.5, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Получатель
                  </Typography>
                </Box>
              </Box>
              
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'rgba(245, 87, 108, 0.1)', color: '#f5576c', width: 48, height: 48 }}>
                    {transfer.targetUser?.fullName?.charAt(0) || 'U'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {transfer.targetUser?.fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      @{transfer.targetUser?.username}
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {transfer.targetUser?.email}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <Fade in={mounted} timeout={900}>
            <Card sx={{ borderRadius: 3 }}>
              <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 2.5, color: 'white' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DescriptionIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Элементы передачи
                  </Typography>
                  <Chip 
                    label={transfer.items.length}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                  />
                </Box>
              </Box>
              
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  {transfer.items.map((item, index) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          borderRadius: 2,
                          transition: 'all 0.3s',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            transform: 'translateY(-4px)',
                          }
                        }}
                      >
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
                            {item.sourceTable?.name || 'Неизвестная таблица'}
                          </Typography>
                          
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            ID записи: {item.sourceDataId.substring(0, 8)}...
                          </Typography>
                          
                          {item.targetTable && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Целевая таблица: {item.targetTable.name}
                            </Typography>
                          )}
                          
                          {item.targetDataId && (
                            <Typography variant="body2" color="text.secondary">
                              ID целевой записи: {item.targetDataId.substring(0, 8)}...
                            </Typography>
                          )}
                        </CardContent>
                        <CardActions sx={{ pt: 0, px: 2, pb: 2 }}>
                          <Button 
                            startIcon={<ViewIcon />}
                            size="small"
                            onClick={() => handleViewItem(item.sourceTableId, item.sourceDataId)}
                            sx={{ color: '#667eea' }}
                          >
                            Исходная запись
                          </Button>
                          
                          {item.targetDataId && (
                            <Button 
                              startIcon={<ViewIcon />}
                              size="small"
                              onClick={() => handleViewItem(item.targetTableId!, item.targetDataId!)}
                              sx={{ color: '#f5576c' }}
                            >
                              Целевая запись
                            </Button>
                          )}
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
      
      {transfer.status === TransferStatus.PENDING && (
        <Fade in={mounted} timeout={1000}>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {isReceiver && (
              <>
                <Button 
                  variant="outlined" 
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => handleStatusDialogOpen(TransferStatus.REJECTED)}
                  sx={{ 
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 }
                  }}
                >
                  Отклонить
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleStatusDialogOpen(TransferStatus.COMPLETED)}
                  sx={{
                    background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
                    }
                  }}
                >
                  Принять
                </Button>
              </>
            )}
            
            {isSender && (
              <Button 
                variant="outlined" 
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => handleStatusDialogOpen(TransferStatus.CANCELLED)}
                sx={{ 
                  borderWidth: 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Отменить
              </Button>
            )}
          </Box>
        </Fade>
      )}
      
      <ConfirmDialog 
        open={statusDialog.open} 
        title={statusDialog.title} 
        message={statusDialog.message} 
        confirmText="Да" 
        cancelText="Нет" 
        onConfirm={handleUpdateStatus} 
        onCancel={handleStatusDialogClose} 
        severity={statusDialog.status === TransferStatus.COMPLETED ? 'info' : 'warning'} 
      />
      
      <AlertMessage 
        open={!!error} 
        message={error || ''} 
        severity="error" 
        onClose={() => setError(null)} 
      />
      
      <AlertMessage 
        open={!!success} 
        message={success || ''} 
        severity="success" 
        onClose={() => setSuccess(null)} 
      />
    </Box>
  );
}